import { ReactNode } from "react";
import { MoreHorizontal, Trash2, Flag } from "lucide-react";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { ModelInfo } from "@jammable/clientapi";
import Link from "next/link";
import Image from "next/image";
import Dropdown from "../../../../../../../apps/web/components/dropdown/Dropdown";
import { VoiceImage, joinArtistNames, timeAgo } from "../../utils";

export interface HeaderProps {
  signedInUser?: { _id?: string };
  shortid: string;
  user: {
    _id?: string;
    avatar: string;
    username: string;
  };
  userId?: string;
  downloads?: number;
  conversionType?: string;
  models?: ModelInfo[];
  model?: { artistname: string; urlParam: string; image: { url: string } };
  name?: string;
  status?: string;
  inputFiles?: any;
  createdAt?: string;
  actionHandler?: (actionKey: "delete" | "report") => void;
  // full: title block above the player (your cover, failed cover).
  // compact: just the "you can leave" line and the menu (still being made).
  // hero: the lavender shared-link hero; `children` is the player.
  variant?: "full" | "compact" | "hero";
  children?: ReactNode;
}

const MenuItem = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => (
  <DropdownMenuItem
    onClick={onClick}
    className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-red-600 tw-outline-none hover:tw-bg-red-50 focus:tw-bg-red-50"
  >
    {children}
  </DropdownMenuItem>
);

export const CoverMenu = ({
  isAuthor,
  actionHandler,
  className = "",
}: {
  isAuthor: boolean;
  actionHandler?: HeaderProps["actionHandler"];
  className?: string;
}) => (
  <Dropdown
    className="tw-rounded-3xl tw-p-1.5"
    dropdownItems={
      <>
        {isAuthor && (
          <MenuItem onClick={() => actionHandler?.("delete")}>
            <Trash2 size={16} />
            Delete cover
          </MenuItem>
        )}
        {!isAuthor && (
          <MenuItem onClick={() => actionHandler?.("report")}>
            <Flag size={16} />
            Report cover
          </MenuItem>
        )}
      </>
    }
  >
    <button
      aria-label="More"
      className={`tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-jam-ink hover:tw-text-jam-purple ${className}`}
    >
      <MoreHorizontal size={20} />
    </button>
  </Dropdown>
);

// Two (or one) voice portraits, the bigger one top-left.
const VoiceStack = ({
  models,
  size,
}: {
  models: Partial<ModelInfo>[];
  size: "md" | "lg";
}) => {
  const [first, second] = models;
  const big =
    size === "lg"
      ? "tw-h-[170px] tw-w-[170px] tw-rounded-[44px] md:tw-h-[240px] md:tw-w-[240px] md:tw-rounded-[56px]"
      : "tw-h-[96px] tw-w-[96px] tw-rounded-[30px]";
  const small =
    size === "lg"
      ? "tw-h-[110px] tw-w-[110px] tw-rounded-[32px] md:tw-h-[150px] md:tw-w-[150px] md:tw-rounded-[40px]"
      : "tw-h-[72px] tw-w-[72px] tw-rounded-[24px]";
  const box =
    size === "lg"
      ? "tw-h-[220px] tw-w-[220px] md:tw-h-[300px] md:tw-w-[300px]"
      : "tw-h-[120px] tw-w-[120px]";
  const border =
    size === "lg"
      ? "tw-border-[6px] tw-border-solid tw-border-white"
      : "tw-border-4 tw-border-solid tw-border-white";

  if (!first) return null;
  return (
    <div className={`tw-relative tw-shrink-0 ${second ? box : ""}`}>
      <div
        className={`${second ? "tw-absolute tw-left-0 tw-top-0" : "tw-relative"} tw-overflow-hidden tw-bg-jam-lav ${big} ${border}`}
      >
        <VoiceImage
          model={first}
          fill
          sizes="240px"
          className="tw-object-cover"
          priority
        />
      </div>
      {second && (
        <div
          className={`tw-absolute tw-bottom-0 tw-right-0 tw-overflow-hidden tw-bg-jam-lav ${small} ${border}`}
        >
          <VoiceImage
            model={second}
            fill
            sizes="150px"
            className="tw-object-cover"
          />
        </div>
      )}
    </div>
  );
};

const SungBy = ({ models }: { models: Partial<ModelInfo>[] }) => (
  <>
    {models.map((m, i) => (
      <span key={m._id || i} className="tw-flex tw-items-center tw-gap-1.5">
        {i > 0 && <span>{i === models.length - 1 ? "&" : ","}</span>}
        <Link
          href={`/${m.urlParam}`}
          className="tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
        >
          {m.artistname}
        </Link>
      </span>
    ))}
  </>
);

export function Header({
  signedInUser,
  shortid,
  user,
  name,
  userId,
  actionHandler,
  models,
  model,
  status,
  createdAt,
  variant = "full",
  children,
}: HeaderProps) {
  const isAuthor = Boolean(
    signedInUser?._id && userId && String(signedInUser._id) === String(userId),
  );

  if (!userId) return null;

  const voices: Partial<ModelInfo>[] =
    models && models.length > 0 ? models : model ? [model as any] : [];
  const artistNames = joinArtistNames(model, models);
  const title = name ? name : artistNames ? `AI ${artistNames} cover` : shortid;
  const ago = timeAgo(createdAt);

  if (variant === "compact") {
    return (
      <header className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {isAuthor
            ? "You can leave this page. We'll finish it and it'll be in your Library."
            : "This cover is still being made."}
        </span>
        <CoverMenu isAuthor={isAuthor} actionHandler={actionHandler} />
      </header>
    );
  }

  if (variant === "hero") {
    return (
      <header className="tw-relative tw-grid tw-items-center tw-gap-6 tw-rounded-[34px] tw-bg-jam-lav tw-p-5 md:tw-grid-cols-[300px_1fr] md:tw-gap-8 md:tw-rounded-[44px] md:tw-p-9">
        <div className="tw-flex tw-justify-center md:tw-block">
          <VoiceStack models={voices} size="lg" />
        </div>
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3.5">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
            <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-jam-ink">
              AI cover{voices.length > 1 ? " · Duet" : ""}
            </span>
            <CoverMenu
              isAuthor={isAuthor}
              actionHandler={actionHandler}
              className="tw-bg-white"
            />
          </div>
          <h1 className="jam-display tw-m-0 tw-break-words tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] tw-text-jam-ink md:tw-text-[52px]">
            {title}
          </h1>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-sm tw-font-bold tw-text-jam-muted">
            {voices.length > 0 && (
              <>
                <span>Voiced by</span>
                <SungBy models={voices} />
              </>
            )}
            {user?.username && (
              <Link
                href={`/@${user.username}`}
                className="tw-flex tw-items-center tw-gap-1.5 tw-text-jam-muted tw-no-underline"
              >
                <span>· Made by</span>
                {user.avatar && (
                  <Image
                    src={user.avatar}
                    alt={user.username}
                    width={22}
                    height={22}
                    className="tw-rounded-full tw-object-cover"
                  />
                )}
                <span className="tw-font-extrabold tw-text-jam-ink">
                  @{user.username}
                </span>
              </Link>
            )}
            {ago && <span>· {ago}</span>}
          </div>
          {children}
        </div>
      </header>
    );
  }

  return (
    <header className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-gap-5">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 sm:tw-contents">
        <VoiceStack models={voices} size="md" />
        <CoverMenu
          isAuthor={isAuthor}
          actionHandler={actionHandler}
          className="sm:tw-hidden"
        />
      </div>
      <div className="tw-flex tw-min-w-0 tw-flex-grow tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
          {status === "success" && (
            <span className="tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-ink">
              Ready
            </span>
          )}
          {status === "failed" && (
            <span className="tw-rounded-full tw-bg-red-50 tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-red-600">
              Didn&apos;t finish
            </span>
          )}
          {voices.length > 1 && (
            <span className="tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-ink">
              Duet
            </span>
          )}
        </div>
        <h1 className="jam-display tw-m-0 tw-break-words tw-text-[30px] tw-font-extrabold tw-leading-[1.05] tw-tracking-[-0.04em] tw-text-jam-ink md:tw-text-[46px] md:tw-leading-none">
          {title}
        </h1>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-sm tw-font-bold tw-text-jam-muted">
          {voices.length > 0 && (
            <>
              <span>Voiced by</span>
              <SungBy models={voices} />
            </>
          )}
          {isAuthor ? (
            <span>· by you</span>
          ) : user?.username ? (
            <span>
              · by{" "}
              <Link
                href={`/@${user.username}`}
                className="tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-text-jam-purple"
              >
                @{user.username}
              </Link>
            </span>
          ) : null}
          {ago && <span>· {ago}</span>}
        </div>
      </div>
      <CoverMenu
        isAuthor={isAuthor}
        actionHandler={actionHandler}
        className="tw-hidden sm:tw-flex sm:tw-self-start"
      />
    </header>
  );
}
